import dynamic from 'next/dynamic';

import React from 'react';

import Footer from '@components/common/Footer';
import Header from '@components/common/Header';
import Banner from '@components/parts/Banner';
import { BigImageTDIs } from '@components/parts/BigImageTDIs';
import { Checklist } from '@components/parts/Checklist';
import { CrossSelling } from '@components/parts/CrossSelling';
import { ExpectedCost } from '@components/parts/ExpectedCost';
import { FAQ } from '@components/parts/FAQ';
import { FullImageSection } from '@components/parts/FullImageSection';
import HeadMeta from '@components/parts/HeadMeta';
import { IconTDIs } from '@components/parts/IconTDIs';
import { Image } from '@components/parts/Image';
import { LinkToBooking } from '@components/parts/LinkToBooking';
import { Metrics } from '@components/parts/Metrics';
import { PartnerBanners } from '@components/parts/PartnerBanners';
import { Quote } from '@components/parts/Quote';
import { Reviews } from '@components/parts/Reviews';
import { SEOStructuredDataComponent } from '@components/parts/SEOStructuredData';
import { ServiceCoverage } from '@components/parts/ServiceCoverage';
import { ServiceScope } from '@components/parts/ServiceScope';
import { TDICards } from '@components/parts/TDICards';
import { Title } from '@components/parts/Title';
import { TitleAndDescription } from '@components/parts/TitleAndDescription';

import { getVisibility } from '@utils/get-visibility';

import { DynamicData } from '@customTypes/DynamicData';
import { GlobalData } from '@customTypes/GlobalData';

const IframeModal = dynamic(() => import('@components/common/IframeModal'), { ssr: false });
const DeepcleanBookingModal = dynamic(() => import('@components/common/DeepcleanBookingModal'), { ssr: false });
const MovingBookingModal = dynamic(() => import('@components/common/MovingBookingModal'), { ssr: false });

type Props = {
  slug: string;
  data: any;
  dynamicData?: DynamicData;
  global: GlobalData;
  pageViewer: string;
};

const Components: Record<string, (...args: any) => JSX.Element | null> = {
  Banner: Banner,
  Header: Header,
  Footer: Footer,
  Title: Title,
  Metrics: Metrics,
  Image: Image,
  Quote: Quote,
  TDICards: TDICards,
  TitleAndDescription: TitleAndDescription,
  PartnerBanners: PartnerBanners,
  IconTDIs: IconTDIs,
  Reviews: Reviews,
  FAQ: FAQ,
  Checklist: Checklist,
  LinkToBooking: LinkToBooking,
  FullImageSection: FullImageSection,
  ServiceScope: ServiceScope,
  FirstServiceScopeTitle: Title,
  FirstServiceScope: ServiceScope,
  SecondServiceScopeTitle: Title,
  SecondServiceScope: ServiceScope,
  ServiceCoverage: ServiceCoverage,
  ExpectedCost: ExpectedCost,
  CrossSelling: CrossSelling,
  BigImageTDIs: BigImageTDIs,
  SEOStructuredData: SEOStructuredDataComponent,
};

const detectComponent = (key: string, value?: any) => {
  if (value?.componentType && Components[value?.componentType]) return Components[value.componentType];
  return Components[key];
};

const DynamicPage: React.FC<Props> = (props) => {
  const { slug, data, global, dynamicData, pageViewer } = props;
  const keys = Object.entries(data);
  const seo = [
    // Merge meta tags
    ...(global.defaultSeo || []),
    ...(data.Seo || []),
    ...(dynamicData?.Seo || []),
  ];
  const structuredData = dynamicData?.SEOStructuredData?.schema;

  return (
    <>
      <IframeModal />
      {seo.map((s, idx) => (
        <HeadMeta key={idx} {...s} />
      ))}
      {structuredData ? <SEOStructuredDataComponent schema={structuredData} /> : null}
      {keys
        .filter(([k]) => k !== 'Seo')
        .filter(([k, v]) => v !== null && detectComponent(k, v))
        .map(([k, v]) => [k, v, detectComponent(k, v)] as const)
        .flatMap(([key, contents, Component]) => {
          if (!Array.isArray(contents)) contents = [contents];
          // @ts-ignore
          return contents.map((content: any, idx: number) => {
            const isVisible = getVisibility(content.viewer_types, pageViewer);
            return isVisible ? (
              <Component key={`${slug}-${key}-${idx}`} dynamicData={dynamicData} {...content} pageViewer={pageViewer} />
            ) : null;
          });
        })}
    </>
  );
};

export default DynamicPage;
